import {
  GoAButton,
  GoAButtonGroup,
  GoACallout,
  GoAContainer,
  GoADivider,
  GoADropdown,
  GoADropdownItem,
  GoAFormItem,
  GoAIcon,
  GoAModal,
  GoASpacer,
  GoASpinner,
  GoATab,
  GoATable,
  GoATabs,
} from '@abgov/react-components';
import styles from './super-user-components.module.css';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import {
  Agency,
  District,
  fetchAllLawEnforcenmentConfigurations,
} from '../../state/slices/lawEnforcementConfigSlice';
import useCsrService from '../../services/csr-api-redux';
import { displaySection } from '../../common/ui-library';
import { set } from 'lodash';
import {
  pushNotification,
  removeAllNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';
import {
  getAllLEOfficers,
  getAllUnassignedLEOfficers,
  getOfficerECollisionLinkOptions,
  linkLEOfficer,
  LinkOption,
  Reviewer,
  updateLEOfficer,
} from '../../state/slices/lawEnforcementOfficersSlice';
import { useNavigate } from 'react-router-dom';
import {
  isAdmin,
  isLawEnforcementAdmin,
  isLawEnforcementOfficer,
  loadCsrUser,
} from '../../state/slices/authSlice';

interface SuperUserDashboardComponetProps {}

export const SuperUserDashboardComponet =
  ({}: SuperUserDashboardComponetProps) => {
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();

    const csrService = useCsrService();
    const officerAgencyErrorText = 'Please select an agency before saving.';
    const officerDistrictErrorText = 'Please select a district before saving.';

    const lawEnforcementConfiguration = useSelector(
      (state: RootState) => state.lawEnfocementConfiguration,
    );
    const lawEnforcementOfficers = useSelector(
      (state: RootState) => state.lawEnforcementLEOfficers,
    );
    const stateAuth = useSelector((state: RootState) => state.auth);

    const [showUpdateOfficerModal, setShowUpdateOfficerModal] = useState(false);
    const [officerAgencyError, setOfficeAgencyError] = useState<string>('');
    const [officerDistrictError, setOfficeDistrictError] = useState<string>('');

    const [agencies, setAgencies] = useState<Agency[]>([]);
    const [reviewers, setReviewers] = useState<Reviewer[]>([]);
    const [selectedSearchAgency, setSelectedSearchAgency] = useState<
      string | null
    >(null);
    const [selectedSearchDistrict, setSelectedSearchDistrict] = useState<
      string | null
    >(null);

    const [selectedAgencyDistricts, setSelectedAgencyDistricts] = useState<
      District[] | null
    >(null);
    const [selectedOfficer, setSelectedOfficer] = useState<Reviewer | null>(
      null,
    );
    const [editAgencyDistricts, setEditAgencyDistricts] = useState<
      District[] | null
    >(null);

    const [selectedUnassignedOfficer, setSelectedUnassignedOfficer] =
      useState<Reviewer | null>(null);

    const [showAssignOfficerModal, setShowAssignOfficerModal] = useState(false);
    const [unassignedReviewers, setUnassignedReviewers] = useState<Reviewer[]>(
      [],
    );
    const [linkOptions, setLinkOptions] = useState<LinkOption[]>([]);
    const [loadLinkOptionsWaitImage, setLoadLinkOptionsWaitImage] =
      useState(false);

    // Clear reviewers on initial load
    useEffect(() => {
      setReviewers([]);
    }, []);

    useEffect(() => {
      if (!stateAuth.auth.access.access) navigate('/');

      if (!stateAuth.auth.csrUser && !stateAuth.isLoading) {
        dispatch(loadCsrUser({ csrService }));
        return;
      }

      if (stateAuth.isLoading || !stateAuth.auth.csrUser) return;

      if (
        stateAuth.auth.csrUser &&
        !isAdmin(stateAuth) &&
        !isLawEnforcementAdmin(stateAuth) &&
        !isLawEnforcementOfficer(stateAuth)
      ) {
        dispatch(
          pushNotification({
            body: 'You do not have permission to view this page.',
            notificationType: NotificationType.importantGenericError,
          }),
        );
        navigate('/');
      } else if (isLawEnforcementOfficer(stateAuth)) {
        dispatch(
          pushNotification({
            body: 'You do not have permission to view this page.',
            notificationType: NotificationType.importantGenericError,
          }),
        );
        navigate('/law');
      }
    }, [stateAuth.auth.csrUser, navigate]);

    useEffect(() => {
      if (stateAuth.auth.csrUser && isAdmin(stateAuth)) {
        dispatch(fetchAllLawEnforcenmentConfigurations({ csrService }));
        dispatch(getAllUnassignedLEOfficers({ csrService }));
      }
    }, [stateAuth.auth.csrUser, navigate]);

    useEffect(() => {
      if (selectedSearchAgency) {
        const agency = agencies.find((a) => a.code === selectedSearchAgency);
        if (agency) {
          // Only fetch officers if we have a valid agency
          dispatch(getAllLEOfficers({ agency: selectedSearchAgency, csrService }));
          setSelectedAgencyDistricts(agency.districts || []);
        } else {
          setSelectedAgencyDistricts([]);
        }
        // Clear district selection when agency changes
        setSelectedSearchDistrict(null);
      } else {
        setSelectedAgencyDistricts([]);
        setSelectedSearchDistrict(null);
        // Clear reviewers when no agency is selected
        setReviewers([]);
      }
    }, [selectedSearchAgency]);

    useEffect(() => {
      if (lawEnforcementOfficers.reviewers) {
        // If no agency is selected, clear the reviewers list
        if (!selectedSearchAgency) {
          setReviewers([]);
          return;
        }

        let foundReviewers = lawEnforcementOfficers.reviewers;

        // Filter by agency if selected
        if (selectedSearchAgency) {
          foundReviewers = foundReviewers.filter(
            (reviewer: Reviewer) => reviewer.agency === selectedSearchAgency
          );
        }

        // Filter by district if selected
        if (selectedSearchDistrict) {
          foundReviewers = foundReviewers.filter(
            (reviewer: Reviewer) => reviewer.district === selectedSearchDistrict
          );
        }

        setReviewers(foundReviewers);
      }
    }, [selectedSearchAgency, selectedSearchDistrict, lawEnforcementOfficers.reviewers]);

    useEffect(() => {
      if (showAssignOfficerModal && selectedUnassignedOfficer) {
        setLoadLinkOptionsWaitImage(true);
        dispatch(
          getOfficerECollisionLinkOptions({
            reviewer: selectedUnassignedOfficer,
            csrService,
          }),
        );
      }
    }, [showAssignOfficerModal]);

    useEffect(() => {
      if (lawEnforcementOfficers.linkOptions) {
        console.log(lawEnforcementOfficers.linkOptions);
        setLinkOptions(lawEnforcementOfficers.linkOptions);
        setLoadLinkOptionsWaitImage(false);
      }
    }, [lawEnforcementOfficers.linkOptions]);

    useEffect(() => {
      if (lawEnforcementOfficers.unassignedReviewers) {
        setUnassignedReviewers(lawEnforcementOfficers.unassignedReviewers);
      }
    }, [lawEnforcementOfficers.unassignedReviewers]);

    useEffect(() => {
      setAgencies(lawEnforcementConfiguration.configuration.agencies || []);
    }, [lawEnforcementConfiguration.configuration.agencies]);

    useEffect(() => {
      if (selectedOfficer) {
        setEditAgencyDistricts(
          agencies.find((a) => a.code === selectedOfficer.agency)?.districts!,
        );
      }
    }, [selectedOfficer]);

    const linkAccount = (
      unassignedOfficer: Reviewer | null,
      linkOption: LinkOption,
    ) => {
      const runLink = async (updatedReviewer: Reviewer) => {
        if (updatedReviewer)
          try {
            await dispatch(
              linkLEOfficer({ reviewer: updatedReviewer, csrService }),
            ).unwrap();

            dispatch(
              pushNotification({
                body: `${updatedReviewer.name} has been linked to ecollision`,
                notificationType: NotificationType.informationGeneric,
              }),
            );
          } catch (error: any) {
            dispatch(
              pushNotification({
                body: 'Error updating officer.',
                notificationType: NotificationType.importantGenericError,
              }),
            );
          }
      };

      if (unassignedOfficer && linkOption) {
        unassignedOfficer.agency = linkOption.agency;
        unassignedOfficer.district = linkOption.district;
        unassignedOfficer.badgeNumber = linkOption.badgeNumber;

        runLink(unassignedOfficer);
      }
    };

    const validOfficerDistrict = (): boolean => {
      let valid = true;
      if (selectedOfficer && !selectedOfficer.agency) {
        setOfficeAgencyError(officerAgencyErrorText);
      }
      valid = false;
      if (selectedOfficer && !selectedOfficer.district) {
        setOfficeDistrictError(officerDistrictErrorText);
        valid = false;
      }

      return true;
    };

    const saveOfficerAgencyDistrict = () => {
      const runSave = async () => {
        if (selectedOfficer)
          try {
            await dispatch(
              updateLEOfficer({ reviewer: selectedOfficer, csrService }),
            ).unwrap();

            dispatch(
              pushNotification({
                body: 'User successfully reassigned to the selected police agency and distric/detachment.',
                notificationType: NotificationType.informationGeneric,
              }),
            );
          } catch (error: any) {
            dispatch(
              pushNotification({
                body: 'Error updating officer.',
                notificationType: NotificationType.importantGenericError,
              }),
            );
          }
      };

      if (validOfficerDistrict()) {
        dispatch(removeAllNotifications());
        runSave();
      } else {
        setOfficeAgencyError(officerAgencyErrorText);
      }
    };

    return (
      <div>
        <GoAModal
          calloutVariant="important"
          open={showUpdateOfficerModal}
          heading="Confirm reassignment"
          actions={
            <GoAButtonGroup alignment="end">
              <GoAButton
                type="tertiary"
                onClick={() => {
                  setShowUpdateOfficerModal(false);
                }}
              >
                Cancel
              </GoAButton>
              <GoAButton
                onClick={() => {
                  setShowUpdateOfficerModal(false);
                  saveOfficerAgencyDistrict();
                  setSelectedOfficer(null);
                }}
              >
                Confirm
              </GoAButton>
            </GoAButtonGroup>
          }
        >
          You are about to reassign this user to a new police agency or
          district/ detachment. Click 'Confirm' to proceed or 'Cancel' to go
          back.
        </GoAModal>
        <GoAModal
          open={showAssignOfficerModal}
          actions={
            <GoAButtonGroup alignment="end">
              <GoAButton
                type="tertiary"
                onClick={() => {
                  setShowAssignOfficerModal(false);
                }}
              >
                Close
              </GoAButton>
            </GoAButtonGroup>
          }
          width="400px"
          maxWidth="400px"
        >
          <h3>Report My Collision officer</h3>
          <GoASpacer vSpacing={'m'} />
          <strong>First name</strong>
          <div>{selectedUnassignedOfficer?.firstName}</div>
          <GoASpacer vSpacing={'m'} />
          <strong>Surname</strong>
          <div>{selectedUnassignedOfficer?.lastName}</div>
          <GoASpacer vSpacing={'m'} />
          <strong>Email</strong>
          <div>{selectedUnassignedOfficer?.email}</div>
          <GoASpacer vSpacing={'m'} />
          <GoADivider />
          <GoASpacer vSpacing={'m'} />
          <h3>eCollision officers</h3>
          <GoASpacer vSpacing={'m'} />
          <div
            style={{
              display: displaySection(loadLinkOptionsWaitImage),
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <GoASpinner size="xlarge" type="infinite"></GoASpinner>
            </div>
          </div>
          <div
            style={{
              display: displaySection(!loadLinkOptionsWaitImage),
              width: '100%',
              maxHeight: '400px',
            }}
          >
            {linkOptions
              ? linkOptions.map((linkOption) => (
                  <div key={`${linkOption.agency}-${linkOption.district}`}>
                    <GoAContainer accent="thin" type="interactive">
                      <div style={{ font: 'var(--goa-typography-body-l)' }}>
                        {linkOption.firstName} {linkOption.lastName}
                      </div>
                      <GoASpacer vSpacing={'m'} />

                      <div>
                        <strong>Agency</strong>
                        <div>
                          {lawEnforcementConfiguration.configuration.agencies &&
                          selectedUnassignedOfficer
                            ? lawEnforcementConfiguration.configuration.agencies.find(
                                (x) => x.code == linkOption.agency,
                              )?.short_description
                            : ''}
                        </div>
                      </div>
                      <div>
                        <strong>District</strong>
                        <div>
                          {lawEnforcementConfiguration.configuration.agencies &&
                          selectedUnassignedOfficer
                            ? lawEnforcementConfiguration.configuration.agencies
                                .find((x) => x.code == linkOption.agency)
                                ?.districts.find(
                                  (x) => x.code == linkOption.district,
                                )?.short_description
                            : ''}
                        </div>
                      </div>

                      <strong>Email address</strong>
                      <div>{linkOption.email}</div>
                      <GoASpacer vSpacing={'l'} />
                      <GoAButtonGroup alignment="start">
                        <GoAButton
                          onClick={() => {
                            linkAccount(selectedUnassignedOfficer, linkOption);
                            setShowAssignOfficerModal(false);
                          }}
                        >
                          Link this account
                        </GoAButton>
                      </GoAButtonGroup>
                    </GoAContainer>
                  </div>
                ))
              : ''}
          </div>
        </GoAModal>
        <GoASpacer vSpacing={'xl'} />
        <h1>Super user dashboard</h1>
        <GoASpacer vSpacing={'xl'} />
        Manage law enforcement officers for the Report My Collision service. Use
        this dashboard to link officer accounts to eCollision and reassign them
        to the appropriate police agency and district/ detachment.
        <GoASpacer vSpacing={'xl'} />
        <GoATabs>
          <GoATab heading="Re-assign officers">
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ width: '700px' }}>
                  <div className={styles.twoitemrow}>
                    <GoAFormItem label="Police agency">
                      <GoADropdown
                        value={selectedSearchAgency ?? ''}
                        relative={true}
                        onChange={(item, value) => {
                          setSelectedSearchAgency(value.toString());
                          if (value.toString() === '') {
                            setSelectedAgencyDistricts([]);
                            setSelectedSearchDistrict(null);
                            setSelectedOfficer(null);
                          }
                        }}
                        width="310"
                        filterable={true}
                      >
                        {agencies.map((agency) => (
                          <GoADropdownItem
                            key={agency.code}
                            value={agency.code}
                            label={agency.short_description}
                          />
                        ))}
                      </GoADropdown>
                    </GoAFormItem>
                    <GoAFormItem label="Police district/detachment">
                      <GoADropdown
                        width="310px"
                        filterable={true}
                        value={selectedSearchDistrict ?? ''}
                        relative={true}
                        onChange={(item, value) => {
                          setSelectedSearchDistrict(value.toString());
                          setSelectedOfficer(null);
                        }}
                      >
                        {selectedAgencyDistricts?.map((district) => (
                          <GoADropdownItem
                            key={district.code}
                            value={district.code}
                            label={district.description}
                          />
                        ))}
                      </GoADropdown>
                    </GoAFormItem>
                  </div>
                </div>
                <div
                  style={{
                    display: displaySection(reviewers.length > 0),
                    width: '650px',
                  }}
                >
                  <GoASpacer vSpacing={'xl'} />
                  <div className={styles.tableContainer}>
                    <GoATable>
                      <tbody>
                        {reviewers.map((reviewer) => (
                          <tr
                            style={
                              selectedOfficer &&
                              reviewer.badgeNumber ==
                                selectedOfficer.badgeNumber
                                ? {
                                    backgroundColor:
                                      reviewer.badgeNumber ===
                                      selectedOfficer?.badgeNumber
                                        ? '#0070C4'
                                        : 'white',
                                    color:
                                      reviewer.badgeNumber ===
                                      selectedOfficer?.badgeNumber
                                        ? 'white'
                                        : 'black',
                                  }
                                : {}
                            }
                            key={`${reviewer.badgeNumber}-${reviewer.email}`}
                          >
                            <td>{reviewer.name}</td>
                            <td>
                              <a
                                style={{
                                  color:
                                    reviewer.badgeNumber ===
                                    selectedOfficer?.badgeNumber
                                      ? 'white'
                                      : '#0070C4',
                                }}
                                href="#"
                                onClick={() => {
                                  setSelectedOfficer({ ...reviewer });
                                }}
                              >
                                View
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </GoATable>
                  </div>
                </div>
                <div style={{ display: displaySection(reviewers.length == 0) }}>
                  <div
                    style={{
                      height: '500px',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <div className={styles.circle}>
                        <ion-icon
                          name="documents-outline"
                          style={{
                            fontSize: '5rem',
                            color: '#A5C3DC',
                          }}
                        ></ion-icon>
                      </div>
                      <div
                        style={{
                          color: '#004F84',
                          width: '225px',
                          textAlign: 'center',
                        }}
                      >
                        Police agency and district information will appear here
                      </div>
                    </div>
                  </div>
                </div>
                <GoASpacer vSpacing={'3xl'} />
              </div>
              <div
                style={{
                  display: displaySection(selectedOfficer !== null),
                  maxWidth: '400px',
                }}
              >
                <GoAContainer accent="thin" type="interactive">
                  <h3>Re-assign officer</h3>
                  <GoASpacer vSpacing={'m'} />
                  Corem lipsum dolor sit amet, consectetur adipiscing elit. Nunc
                  vulputate libero et velit interdum, ac aliquet odio mattis.
                  <GoASpacer vSpacing={'m'} />
                  <strong>Current reviewer</strong>
                  {selectedOfficer?.name}
                  <GoASpacer vSpacing={'m'} />
                  <strong>Current agency</strong>
                  {lawEnforcementConfiguration.configuration.agencies &&
                  selectedOfficer
                    ? lawEnforcementConfiguration.configuration.agencies.find(
                        (x) => x.code == selectedOfficer.agency,
                      )?.short_description
                    : ''}
                  <GoASpacer vSpacing={'m'} />
                  <strong>Current district</strong>
                  {lawEnforcementConfiguration.configuration.agencies &&
                  selectedOfficer
                    ? lawEnforcementConfiguration.configuration.agencies
                        .find((x) => x.code == selectedOfficer.agency)
                        ?.districts.find(
                          (x) => x.code == selectedOfficer.district,
                        )?.short_description
                    : ''}
                </GoAContainer>
                Use the filters below to reassign the officer to a different
                agency and/or district.
                <GoASpacer vSpacing={'m'} />
                <GoAFormItem label="Police agency" error={officerAgencyError}>
                  <GoADropdown
                    value={selectedOfficer?.agency ?? ''}
                    width="375px"
                    filterable={true}
                    relative={true}
                    onChange={(item, value) => {
                      if (value && selectedOfficer) {
                        selectedOfficer.agency = value.toString();
                        setEditAgencyDistricts(
                          agencies.find((a) => a.code === value)?.districts!,
                        );
                      } else if (selectedOfficer) {
                        selectedOfficer.agency = '';
                        selectedOfficer.district = '';
                        setEditAgencyDistricts([]);
                      }
                    }}
                    error={officerAgencyError.length > 0}
                  >
                    {agencies.map((agency) => (
                      <GoADropdownItem
                        key={agency.code}
                        value={agency.code}
                        label={agency.short_description}
                      />
                    ))}
                  </GoADropdown>
                </GoAFormItem>
                <GoASpacer vSpacing={'m'} />
                <GoAFormItem
                  label="Police district"
                  helpText="If you assign a reviewer to a previously unassigned collision report the status will change to Under Review"
                  error={officerDistrictError}
                >
                  <GoADropdown
                    value={selectedOfficer?.district ?? ''}
                    width="375px"
                    filterable={true}
                    relative={true}
                    onChange={(item, value) => {
                      if (value && selectedOfficer)
                        selectedOfficer.district = value.toString();
                      else if (selectedOfficer) selectedOfficer.district = '';
                    }}
                    error={officerDistrictError.length > 0}
                  >
                    {editAgencyDistricts?.map((district) => (
                      <GoADropdownItem
                        key={district.code}
                        value={district.code}
                        label={district.short_description}
                      />
                    ))}
                  </GoADropdown>
                </GoAFormItem>
                <GoASpacer vSpacing={'2xl'} />
                <GoAButtonGroup alignment="start">
                  <GoAButton
                    onClick={() => {
                      setOfficeAgencyError('');
                      setOfficeDistrictError('');
                      setShowUpdateOfficerModal(true);
                    }}
                  >
                    Re-assign
                  </GoAButton>
                  <GoAButton
                    type="tertiary"
                    onClick={() => {
                      setSelectedOfficer(null);
                    }}
                  >
                    Cancel
                  </GoAButton>
                </GoAButtonGroup>
                <GoASpacer vSpacing={'3xl'} />
              </div>
            </div>
          </GoATab>

          <GoATab heading="Unlinked officers">
            <GoATable>
              <thead>
                <tr>
                  <th>Agency</th>
                  <th>Officer name</th>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {unassignedReviewers.map((reviewer) => (
                  <tr
                    style={
                      selectedUnassignedOfficer &&
                      reviewer.badgeNumber ==
                        selectedUnassignedOfficer.badgeNumber
                        ? {
                            backgroundColor:
                              reviewer.email ===
                              selectedUnassignedOfficer?.email
                                ? '#0070C4'
                                : 'white',
                            color:
                              reviewer.email ===
                              selectedUnassignedOfficer?.email
                                ? 'white'
                                : 'black',
                          }
                        : {}
                    }
                    key={reviewer.badgeNumber}
                  >
                    <td>{reviewer.orgName}</td>
                    <td>{reviewer.name}</td>
                    <td>{reviewer.email}</td>
                    <td>
                      <a
                        style={{
                          color:
                            reviewer.email === selectedUnassignedOfficer?.email
                              ? 'white'
                              : '#0070C4',
                        }}
                        href="#"
                        onClick={() => {
                          setSelectedUnassignedOfficer({ ...reviewer });
                          setShowAssignOfficerModal(true);
                        }}
                      >
                        View
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </GoATable>
          </GoATab>
        </GoATabs>
      </div>
    );
  };
